// Admin's own shell, as every admin board draws it: the black sidebar with the wordmark, the menu and
// who's logged in, and the page beside it, in Adam's master brand. Under 760px the sidebar becomes a
// bar with a Menu button (Adam, 4 Oct, answer 5). For owner and admin on a phone, a second row under the bar holds
// Today, Masterminds and People, the 3 screens of an evening's work, with a thin gold bar under the 1 that's open (the
// critique's admin 16; gold never carries text); everything else stays in Menu. Admin isn't drawn inside the member
// shell.
//
// AdminFrame is the frame alone (the 2-step screens use it with no menu); AdminShell is the frame with
// the menu for a role, the flash, and the screen for the address (screens.js), or a holding panel
// until that screen's file exists. An owner or admin opening it asks for the day's clear-ups
// (shell/tidy.js), quietly.
// admin.css first, before the screens and their own stylesheets: the bundle keeps the order the
// stylesheets are imported in, so a screen's own rule beats a shared .ad- rule of the same weight
import '../styles/admin.css'
import { Component, useEffect, useRef, useState } from 'react'
import { Link, usePath, navigate } from '../lib/router.jsx'
import { usePhone } from '../lib/media.js'
import { initials } from '../lib/format.js'
import { useMember } from '../app/member.jsx'
import { AdminProvider, useAdmin } from './shell/context.jsx'
import { Icon } from './shell/Icons.jsx'
import { navFor, MEMBER_SIDE, ROLE_NAMES, SCREENS, canOpen, parseAdminRoute, redirectFor } from './shell/routes.js'
import { loadAdminScreen, knownAdminScreen } from './screens.js'
import { Holding, NotForRole, NoSuchPage, ScreenFailed } from './shell/Holding.jsx'
import { Flash } from './ui/index.js'
import { useTidyOnOpen } from './shell/tidy.js'

// The owner's 1 quiet line when the day's clear-up of old files didn't finish (undrawn)
export const TIDY_FAILED = "The clear-up of old files didn't finish today. Nothing members see has changed, and what's left is cleared another day."


function Wordmark() {
  return (
    <Link to="/admin" className="ad-mark" aria-label="Adam Slee Admin, Today">
      <span className="ad-mark-name">Adam Slee</span>
      <span className="ad-mark-dot" aria-hidden="true" />
      <span className="ad-mark-sub">Admin</span>
    </Link>
  )
}

function NavList({ items, current, onPick }) {
  return (
    <nav className="ad-nav" aria-label="Admin">
      {items.map(item => (
        <Link key={item.key} to={item.to} className="ad-nav-item" aria-current={item.key === current ? 'page' : undefined} onClick={onPick}>
          <Icon name={item.icon} />{item.label}
        </Link>
      ))}
      {items.length > 0 && <div className="ad-nav-rule" role="separator" />}
      <Link to={MEMBER_SIDE.to} className="ad-nav-item" onClick={onPick}><Icon name={MEMBER_SIDE.icon} />{MEMBER_SIDE.label}</Link>
    </nav>
  )
}

function Me({ person, roleLine }) {
  if (!person) return null
  return (
    <div className="ad-me">
      <span className="ad-me-ring" aria-hidden="true">{initials(person.full_name)}</span>
      <span className="ad-me-text">
        <span className="ad-me-name">{person.full_name}</span>
        {roleLine && <span className="ad-me-role">{roleLine}</span>}
      </span>
    </div>
  )
}

// The phone's second row: Today, Masterminds and People, for owner and admin (the critique's admin 16)
export const QUICK = ['today', 'masterminds', 'people']
export const QUICK_LABEL = 'Shortcuts'
export const quickFor = (role, items) => (role === 'owner' || role === 'admin' ? QUICK.map(k => items.find(i => i.key === k)).filter(Boolean) : [])

function QuickRow({ items, current }) {
  if (!items.length) return null
  return (
    <nav className="ad-quick" aria-label={QUICK_LABEL}>
      {items.map(item => (
        <Link key={item.key} to={item.to} className="ad-quick-item" aria-current={item.key === current ? 'page' : undefined}>{item.label}</Link>
      ))}
    </nav>
  )
}

// The frame: the sidebar (or the phone's bar and its menu) and the page. items: the menu for the role,
// empty at the 2-step screens, where only Member side shows. quick: the phone's second row (QUICK), empty but for
// owner and admin.
export function AdminFrame({ items = [], quick = [], current, person, roleLine, children }) {
  const phone = usePhone()
  const [open, setOpen] = useState(false)
  const path = usePath()
  const menuRef = useRef(null)
  const buttonRef = useRef(null)

  // the menu closes when the page changes, and with Escape
  useEffect(() => { setOpen(false) }, [path, phone])
  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus() } }
    const onDown = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [open])

  if (phone) {
    return (
      <div className="ad-app">
        <div className="ad-top" ref={menuRef}>
          <div className="ad-topbar">
            <Wordmark />
            <button ref={buttonRef} type="button" className="ad-menu-button" aria-expanded={open} aria-controls="ad-menu"
              onClick={() => setOpen(o => !o)}>
              <Icon name={open ? 'close' : 'menu'} size={16} />Menu
            </button>
          </div>
          <QuickRow items={quick} current={current} />
          {open && (
            <div className="ad-menu-panel" id="ad-menu">
              <NavList items={items} current={current} onPick={() => setOpen(false)} />
              <Me person={person} roleLine={roleLine} />
            </div>
          )}
        </div>
        <main className="ad-main" id="admin-main">{children}</main>
      </div>
    )
  }
  return (
    <div className="ad-app">
      <aside className="ad-side">
        <div className="ad-side-inner">
          <div className="ad-side-top">
            <Wordmark />
            <NavList items={items} current={current} />
          </div>
          <Me person={person} roleLine={roleLine} />
        </div>
      </aside>
      <main className="ad-main" id="admin-main">{children}</main>
    </div>
  )
}

// A screen that throws is caught here, so the menu stays and the rest of admin still works
class ScreenBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false } }
  static getDerivedStateFromError() { return { failed: true } }
  componentDidCatch(error) { console.error(error) }
  componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false }) }
  render() { return this.state.failed ? <ScreenFailed title={this.props.title} /> : this.props.children }
}

// The screen file for a name, once the loader has answered (null: no file yet, so a holding panel)
function useScreen(name) {
  const [screen, setScreen] = useState(() => (name ? knownAdminScreen(name) : null))
  useEffect(() => {
    let alive = true
    if (!name) { setScreen(null); return undefined }
    const known = knownAdminScreen(name)
    if (known !== undefined) { setScreen(() => known); return undefined }
    setScreen(undefined)
    loadAdminScreen(name).then(s => { if (alive) setScreen(() => s) })
    return () => { alive = false }
  }, [name])
  return screen
}

export default function AdminShell({ role }) {
  const { me } = useMember()
  return (
    <AdminProvider me={me} role={role}>
      <ShellBody />
    </AdminProvider>
  )
}

function ShellBody() {
  const { me, role, api, scopes, products, flash } = useAdmin()
  const path = usePath()
  const route = parseAdminRoute(path)
  const redirect = redirectFor(role, route)
  useEffect(() => { if (redirect) navigate(redirect, { replace: true }) }, [redirect])
  const allowed = !redirect && route.screen && canOpen(role, route.screen)
  const Screen = useScreen(allowed ? route.screen : null)
  const items = navFor(role)
  const current = route.screen ? SCREENS[route.screen].nav : null
  const scopedNames = role === 'host' ? products.filter(p => scopes.includes(p.id)).map(p => p.name) : []
  const roleLine = [ROLE_NAMES[role], ...scopedNames].join(', ')
  const tidyFailed = useTidyOnOpen({ api, role, viewingAs: Boolean(me?.who?.viewing_as) })

  let page
  if (redirect) page = null
  else if (!route.screen) page = <NoSuchPage />
  else if (!allowed) page = <NotForRole title={SCREENS[route.screen].title} />
  else if (Screen === undefined) page = null
  else if (!Screen) page = <Holding title={SCREENS[route.screen].title} />
  else {
    page = (
      <ScreenBoundary title={SCREENS[route.screen].title} resetKey={path}>
        <Screen me={me} role={role} route={route} api={api} scopes={scopes} />
      </ScreenBoundary>
    )
  }

  return (
    <AdminFrame items={items} quick={quickFor(role, items)} current={current} person={me.person} roleLine={roleLine}>
      {flash && <Flash actions={flash.actions} tone={flash.tone}>{flash.text}</Flash>}
      {page}
      {tidyFailed && <p className="ad-quiet-line" role="status">{TIDY_FAILED}</p>}
    </AdminFrame>
  )
}
